import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { QueryModule } from "@/data/queryModules";
import { MutationEffectsOptions, useMutationEffects } from "@/data/useMutationEffects";
import { AppQueryOptions, AppInfiniteQueryOptions, AppMutationOptions } from "@povio/openapi-codegen-cli";
import { EndorsementsModels } from "./endorsements.models";
import { EndorsementsApi } from "./endorsements.api";

export namespace EndorsementsQueries {

export const moduleName = QueryModule.Endorsements;

export const keys = {
    all: [moduleName] as const,
    getIncoming: (limit?: number, order?: string, filter?: EndorsementsModels.EndorsementPaginationFilterDto, page?: number, cursor?: string, ) => [...keys.all, "/endorsements/incoming", limit, order, filter, page, cursor] as const,
    getIncomingInfinite: (limit?: number, order?: string, filter?: EndorsementsModels.EndorsementPaginationFilterDto, cursor?: string, ) => [...keys.all, "/endorsements/incoming", "infinite", limit, order, filter, cursor] as const,
    getOutgoing: (limit?: number, order?: string, filter?: EndorsementsModels.EndorsementPaginationFilterDto, page?: number, cursor?: string, ) => [...keys.all, "/endorsements/outgoing", limit, order, filter, page, cursor] as const,
    getOutgoingInfinite: (limit?: number, order?: string, filter?: EndorsementsModels.EndorsementPaginationFilterDto, cursor?: string, ) => [...keys.all, "/endorsements/outgoing", "infinite", limit, order, filter, cursor] as const,
    getStats: () => [...keys.all, "/endorsements/stats", ] as const,
    getById: (id: string, ) => [...keys.all, "/endorsements/:id", id] as const,
};




 /** 
 * Mutation `useCreateForUser`
 * @summary Create an endorsement
 * @param { string } mutation.id Path parameter. Endorsement ID
 * @param { EndorsementsModels.CreateEndorsementDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.EndorsementDto> } Endorsement created successfully
 * @statusCodes [200, 201, 401]
 */
export const useCreateForUser = (options?: AppMutationOptions<typeof EndorsementsApi.createForUser, { id: string, data: EndorsementsModels.CreateEndorsementDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id, data } ) => 
      
            EndorsementsApi.createForUser(id, data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      const { id } = variables;
      const updateKeys = [keys.getById(id), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useRequest`
 * @summary Request endorsements from multiple email addresses
 * @param { EndorsementsModels.CreateEndorsementRequestDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<EndorsementsModels.RequestResponse> } Endorsement requests created successfully
 * @statusCodes [201, 401]
 */
export const useRequest = (options?: AppMutationOptions<typeof EndorsementsApi.request, { data: EndorsementsModels.CreateEndorsementRequestDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            EndorsementsApi.request(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





 /** 
 * Query `useGetIncoming`
 * @summary Get incoming endorsements by the user
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, userEmail, endorserEmail, status, createdAt, updatedAt, statusChangedAt. Example: `id`
 * @param { EndorsementsModels.EndorsementPaginationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<EndorsementsModels.GetIncomingResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetIncoming = <TData>({ limit, order, filter, page, cursor }: { limit: number, order?: string, filter?: EndorsementsModels.EndorsementPaginationFilterDto, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof EndorsementsApi.getIncoming, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getIncoming(limit, order, filter, page, cursor),
    queryFn: () =>  
    
    EndorsementsApi.getIncoming(limit, order, filter, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetIncomingInfinite
 * @summary Get incoming endorsements by the user
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, userEmail, endorserEmail, status, createdAt, updatedAt, statusChangedAt. Example: `id`
 * @param { EndorsementsModels.EndorsementPaginationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<EndorsementsModels.GetIncomingResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetIncomingInfinite = <TData>({ limit, order, filter, cursor }: { limit: number, order?: string, filter?: EndorsementsModels.EndorsementPaginationFilterDto, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof EndorsementsApi.getIncoming, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getIncomingInfinite(limit, order, filter, cursor),
    queryFn: ({ pageParam }) =>  
    
    EndorsementsApi.getIncoming(limit, order, filter, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};


 /** 
 * Query `useGetOutgoing`
 * @summary Get outgoing endorsements by the user
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, userEmail, endorserEmail, status, createdAt, updatedAt, statusChangedAt. Example: `id`
 * @param { EndorsementsModels.EndorsementPaginationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<EndorsementsModels.GetOutgoingResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetOutgoing = <TData>({ limit, order, filter, page, cursor }: { limit: number, order?: string, filter?: EndorsementsModels.EndorsementPaginationFilterDto, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof EndorsementsApi.getOutgoing, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getOutgoing(limit, order, filter, page, cursor),
    queryFn: () =>  
    
    EndorsementsApi.getOutgoing(limit, order, filter, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetOutgoingInfinite
 * @summary Get outgoing endorsements by the user
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): id, userEmail, endorserEmail, status, createdAt, updatedAt, statusChangedAt. Example: `id`
 * @param { EndorsementsModels.EndorsementPaginationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<EndorsementsModels.GetOutgoingResponse> } 
 * @statusCodes [200, 401]
 */
export const useGetOutgoingInfinite = <TData>({ limit, order, filter, cursor }: { limit: number, order?: string, filter?: EndorsementsModels.EndorsementPaginationFilterDto, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof EndorsementsApi.getOutgoing, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getOutgoingInfinite(limit, order, filter, cursor),
    queryFn: ({ pageParam }) =>  
    
    EndorsementsApi.getOutgoing(limit, order, filter, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};


 /** 
 * Query `useGetStats`
 * @summary Get endorsement statistics for the user
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<EndorsementsModels.EndorsementStatsDto> } Endorsement statistics
 * @statusCodes [200, 401]
 */
export const useGetStats = <TData>(options?: AppQueryOptions<typeof EndorsementsApi.getStats, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getStats(),
    queryFn: EndorsementsApi.getStats
    ,
    ...options,
  });
};






 /** 
 * Query `useGetById`
 * @summary Get a single endorsement by ID
 * @param { string } object.id Path parameter. Endorsement ID
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.EndorsementDto> } Endorsement details
 * @statusCodes [200, 401, 403, 404]
 */
export const useGetById = <TData>({ id }: { id: string,  }, options?: AppQueryOptions<typeof EndorsementsApi.getById, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getById(id),
    queryFn: () =>  
    
    EndorsementsApi.getById(id)
    ,
    ...options,
  });
};








 /** 
 * Mutation `useDeleteEndorsement`
 * @summary Delete an endorsement
 * @param { string } mutation.id Path parameter. Endorsement ID
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } Endorsement deleted successfully
 * @statusCodes [204, 401]
 */
export const useDeleteEndorsement = (options?: AppMutationOptions<typeof EndorsementsApi.deleteEndorsement, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            EndorsementsApi.deleteEndorsement(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useWrite`
 * @summary Write an endorsement (respond to a request)
 * @param { string } mutation.id Path parameter. Endorsement ID
 * @param { EndorsementsModels.WriteEndorsementDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.EndorsementDto> } Endorsement written successfully
 * @statusCodes [200, 201, 401]
 */
export const useWrite = (options?: AppMutationOptions<typeof EndorsementsApi.write, { id: string, data: EndorsementsModels.WriteEndorsementDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id, data } ) => 
      
            EndorsementsApi.write(id, data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      const { id } = variables;
      const updateKeys = [keys.getById(id), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useIgnore`
 * @summary Ignore an endorsement request
 * @param { string } mutation.id Path parameter. Endorsement ID
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.EndorsementDto> } Endorsement ignored successfully
 * @statusCodes [200, 201, 401]
 */
export const useIgnore = (options?: AppMutationOptions<typeof EndorsementsApi.ignore, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            EndorsementsApi.ignore(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      const { id } = variables;
      const updateKeys = [keys.getById(id), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useApprove`
 * @summary Approve an endorsement
 * @param { string } mutation.id Path parameter. Endorsement ID
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.EndorsementDto> } Endorsement approved successfully
 * @statusCodes [200, 201, 401]
 */
export const useApprove = (options?: AppMutationOptions<typeof EndorsementsApi.approve, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            EndorsementsApi.approve(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      const { id } = variables;
      const updateKeys = [keys.getById(id), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useReject`
 * @summary Reject an endorsement
 * @param { string } mutation.id Path parameter. Endorsement ID
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.EndorsementDto> } Endorsement rejected successfully
 * @statusCodes [200, 201, 401]
 */
export const useReject = (options?: AppMutationOptions<typeof EndorsementsApi.reject, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            EndorsementsApi.reject(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      const { id } = variables;
      const updateKeys = [keys.getById(id), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useUpdateVisibility`
 * @summary Update endorsement visibility
 * @param { string } mutation.id Path parameter. Endorsement ID
 * @param { EndorsementsModels.UpdateEndorsementVisibilityDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.EndorsementDto> } Endorsement visibility updated successfully
 * @statusCodes [200, 401]
 */
export const useUpdateVisibility = (options?: AppMutationOptions<typeof EndorsementsApi.updateVisibility, { id: string, data: EndorsementsModels.UpdateEndorsementVisibilityDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id, data } ) => 
      
            EndorsementsApi.updateVisibility(id, data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      const { id } = variables;
      const updateKeys = [keys.getById(id), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





}
